import React, { useState, useMemo } from 'react';
import { useQuery } from 'react-query';
import { communityServicesAPI } from '../../../services/api';
import Pagination from '../../common/Pagination'; 

const AddVolunteerModal = ({ isOpen, onClose, onAddVolunteer }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selected, setSelected] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);

  const { data: volunteersData, isLoading } = useQuery(
    ['community-services', 'volunteers', 'modal'],
    async () => {
      try {
        const res = await communityServicesAPI.getVolunteersList({ page: 1, per_page: 1000 });
        if (!res.data.success) return [];

        const { volunteers } = res.data.data;
        return volunteers.map((v) => ({
          id: v.id,
          id_no: v.volunteer_no || `VOL-${v.id}`,
          name: v.is_resident && v.resident ? v.resident.full_name : v.volunteer_name || 'Unknown',
          contact: v.contact_information || '',
          role: v.role || 'Volunteer',
          raw: v,
        }));
      } catch (err) {
        console.error('Failed to fetch volunteers:', err);
        return [];
      }
    },
    {
      enabled: isOpen,
      staleTime: 30000,
      cacheTime: 60000,
    }
  );


  const filteredItems = useMemo(() => {
    if (!volunteersData) return [];
    const q = searchQuery.toLowerCase().trim();
    return q
      ? volunteersData.filter(
          (v) =>
            (v.name && v.name.toLowerCase().includes(q)) ||
            (v.id_no && v.id_no.toLowerCase().includes(q)) ||
            (v.contact && v.contact.toLowerCase().includes(q))
        )
      : volunteersData;
  }, [volunteersData, searchQuery]);

  const paginatedItems = useMemo(() => {
    const startIndex = (currentPage - 1) * perPage;
    return filteredItems.slice(startIndex, startIndex + perPage);
  }, [filteredItems, currentPage, perPage]);

  const lastPage = Math.ceil(filteredItems.length / perPage);

  const toggleSelectAll = (checked) => {
    if (!checked) return setSelected({});
    const next = {};
    filteredItems.forEach((it) => (next[it.id] = it));
    setSelected(next);
  };

  const toggleRow = (it, checked) => {
    setSelected((prev) => {
      const next = { ...prev };
      if (checked) next[it.id] = it;
      else delete next[it.id];
      return next;
    });
  };

  const handleAssignVolunteers = () => {
    const selectedList = Object.values(selected);
    if (selectedList.length === 0) return;
    onAddVolunteer(selectedList.map((s) => s.raw ?? s));
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100] p-4">
      <div className="bg-white rounded-xl shadow-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-xl font-bold text-gray-900">Add Volunteer</h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">✕</button>
          </div>

          <input
            type="text"
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setCurrentPage(1); 
            }}
            placeholder="Search by name, ID number, or contact..."
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 mb-4"
          />

          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <table className="min-w-full">
              <thead className="bg-gray-50 text-gray-700 text-xs uppercase">
                <tr>
                  <th className="px-4 py-3 w-10">
                    <input
                      type="checkbox"
                      checked={filteredItems.length > 0 && filteredItems.every((it) => selected[it.id])}
                      onChange={(e) => toggleSelectAll(e.target.checked)}
                    />
                  </th>
                  <th className="px-4 py-3 text-left">ID No.</th>
                  <th className="px-4 py-3 text-left">Name</th>
                  <th className="px-4 py-3 text-left">Contact</th>
                  <th className="px-4 py-3 text-left">Role</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 text-sm">
                {isLoading ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-6 text-center text-gray-500">
                      Loading volunteers...
                    </td>
                  </tr>
                ) : paginatedItems.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-6 text-center text-gray-500">
                      No volunteers found
                    </td>
                  </tr>
                ) : (
                  paginatedItems.map((it) => (
                    <tr key={it.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <input
                          type="checkbox"
                          checked={!!selected[it.id]}
                          onChange={(e) => toggleRow(it, e.target.checked)}
                        />
                      </td>
                      <td className="px-4 py-3">{it.id_no}</td>
                      <td className="px-4 py-3">{it.name}</td>
                      <td className="px-4 py-3">{it.contact}</td>
                      <td className="px-4 py-3 capitalize">{it.role}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="mt-4">
            <Pagination
              currentPage={currentPage}
              lastPage={lastPage}
              perPage={perPage}
              total={filteredItems.length}
              onPageChange={(page, size) => {
                setCurrentPage(page);
                if (size) setPerPage(size);
              }}
              onPerPageChange={(size) => {
                setPerPage(size);
                setCurrentPage(1);
              }}
              isLoading={isLoading}
            />
          </div>

          <div className="mt-6 flex justify-end gap-3">
            <button
              onClick={onClose}
              className="px-6 py-3 border border-gray-300 rounded-lg"
            >
              Cancel
            </button>
            <button
              onClick={handleAssignVolunteers}
              disabled={Object.keys(selected).length === 0}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg disabled:opacity-50"
            >
              Assign Volunteer
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddVolunteerModal;